import { WarningIcon, RepeatIcon } from "@chakra-ui/icons";
import {
  Button,
  Text,
  VStack,
  Box,
  Icon,
  useColorModeValue,
  Heading,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { FiAlertTriangle, FiRefreshCw, FiWifiOff } from "react-icons/fi";

const MotionVStack = motion(VStack);
const MotionBox = motion(Box);

// Animation variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const iconVariants = {
  hidden: { scale: 0.5, opacity: 0 },
  visible: {
    scale: 1,
    opacity: 1,
    transition: {
      delay: 0.2,
      type: "spring",
      stiffness: 200,
      damping: 15,
    },
  },
};

function ErrorAlert({
  message = "Terjadi kesalahan",
  title,
  onRetry,
  retryText = "Coba Lagi",
  type = "error", // error | network | warning
  ...props
}) {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.100", "gray.700");

  const errorConfig = {
    error: {
      icon: FiAlertTriangle,
      color: "red",
      defaultTitle: "Terjadi Kesalahan",
    },
    network: {
      icon: FiWifiOff,
      color: "orange",
      defaultTitle: "Koneksi Bermasalah",
    },
    warning: {
      icon: FiAlertTriangle,
      color: "yellow",
      defaultTitle: "Perhatian",
    },
  };

  const config = errorConfig[type] || errorConfig.error;
  const displayTitle = title || config.defaultTitle;

  return (
    <MotionVStack
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      gap={4}
      flexGrow={1}
      py={12}
      px={6}
      align="center"
      justify="center"
      bg={bgColor}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      maxW="400px"
      mx="auto"
      {...props}
    >
      <MotionBox
        variants={iconVariants}
        initial="hidden"
        animate="visible"
        p={4}
        borderRadius="full"
        bg={`${config.color}.50`}
      >
        <Icon
          as={config.icon}
          w={10}
          h={10}
          color={`${config.color}.500`}
        />
      </MotionBox>

      <VStack spacing={2}>
        <Heading size="md" color="gray.700" textAlign="center">
          {displayTitle}
        </Heading>
        <Text
          fontSize="sm"
          color="gray.500"
          textAlign="center"
          maxW="300px"
        >
          {message}
        </Text>
      </VStack>

      {onRetry && (
        <Button
          size="md"
          colorScheme="brand"
          leftIcon={<FiRefreshCw />}
          onClick={onRetry}
          mt={2}
          _hover={{
            transform: "translateY(-2px)",
          }}
          transition="all 0.2s"
        >
          {retryText}
        </Button>
      )}
    </MotionVStack>
  );
}

// Full page error component
export function FullPageError({ message, onRetry, type = "error" }) {
  const bgColor = useColorModeValue("gray.50", "gray.900");

  return (
    <Box
      height="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={bgColor}
      p={4}
    >
      <ErrorAlert
        message={message}
        onRetry={onRetry}
        type={type}
      />
    </Box>
  );
}

// Section error (inline)
export function SectionError({ message, onRetry, type = "error", compact = false }) {
  if (compact) {
    return (
      <VStack py={4} spacing={2} align="center">
        <Icon as={FiAlertTriangle} color="red.500" boxSize={5} />
        <Text fontSize="sm" color="gray.500">{message}</Text>
        {onRetry && (
          <Button size="xs" variant="ghost" onClick={onRetry} leftIcon={<FiRefreshCw />}>
            Retry
          </Button>
        )}
      </VStack>
    );
  }

  return (
    <ErrorAlert
      message={message}
      onRetry={onRetry}
      type={type}
    />
  );
}

export default ErrorAlert;
